/**
 * FirmaAnasayfaListPage.jsx — Firma Ana Sayfası (Dekoratör görünümü)
 * Route: /Firma/Anasayfa
 *
 * Kullanıcı grubu 9 (Firma Moderatör) ve 19 (Firma Admin) için giriş sonrası
 * tek ulaşılabilir sayfa. Bu sürüm sadece DEKORATÖR olarak giriş yapan bir
 * firmayı hedefler — birden çok müşteri firmanın (katılımcı) standlarını
 * işleten bir dekoratörün kendi profili + personel kadrosu + müşteri
 * roster'ının inşa izni/proje evrakı durumu + kendi fuar evrakları.
 *
 * KATILIMCI (exhibitor) girişi için sayfa ayrı bir geçişte ele alınacak —
 * o durumda "İnşa İzin Durumu"/"Proje Evrakları Durumu" panelleri kendi
 * standlarına (tek firma adı, birden çok stand olabilir) döner ve dekoratör
 * atanmamış uyarısı bu sayfada değil o varyantta anlamlıdır.
 *
 * Firma profili/personel/fuar evrakları: firmaProfileService.js (yeni varlık).
 * Müşteri roster'ı (İnşa İzin/Proje Evrakları): standOverviewService.js'teki
 * aynı stand verisi, dekoratorFirmaId = bu firma olacak şekilde süzülmüş.
 *
 * firmaId, gerçek backend login/user endpoint'i bu alanı dönene kadar mock
 * olarak localStorage'daki 'user' nesnesinden okunur, yoksa MOCK_DECORATOR_FIRMA_ID
 * (test hesabı testfirmamod için) kullanılır.
 *
 * 2026-08-11 reconciliation (bkz. util/knowledgebase/local-backend-testing.md "forceLive
 * pattern"): kardeş sayfalarla (FirmaStandListPage.jsx/FirmaProjelerListPage.jsx/
 * FirmaTaleplerListPage.jsx) AYNI selectedFuarId ∈ {298,299} tetikleyicisi — firmaId
 * TEST_DECORATOR_FIRMA_ID'ye (49858) sabitlenir, tüm widget veri kaynakları forceLive
 * dalına geçer: getFirmaProfile/getFirmaDocuments/getFirmaBelgeleri (firmaProfileService),
 * "Personeller" artık firmaProfileService.getFirmaPersonnel (hiç canlı dalı yoktu) DEĞİL,
 * kardeş Personel Listesi sayfasının zaten reconcile edilmiş firmaPersonelListService.
 * getFirmaPersonelListesi'si — iki ayrı personel kaynağının sürüklenmesini önlemek için.
 * clientStands (Stand Durumu/Standlarım/Projeler/Talepler widget'ları) getStandOverviewList
 * + standId köprüsüyle projectService.getProjectByStandId (mergeLiveProjectIntoStand, bkz.
 * aşağıdaki dosya-yerel kopya) — kardeş sayfalardaki AYNI desen. "İletişim" widget'ı
 * #538'den beri canlı: `GET /firma/{firmaId}/iletisim-kisileri` (backend #537), satırları personel
 * Sistem Ayarları › İletişim Kişileri sayfasından yönetiyor. (WAS: backend'de uç yoktu, widget
 * firmaProfileService.js'teki sabit bir Teknik Destek Hattı satırına düşüyordu.)
 */
import React, { useContext, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container, Card, CardHeader, CardBody, Collapse, Row, Col, Button, Badge, Spinner, Alert,
  Dropdown, DropdownToggle, DropdownMenu, DropdownItem,
} from "reactstrap";
import {
  Building2, Phone, Mail, Globe, MapPin, ChevronUp, ChevronDown, User,
  Check, X, ArrowRight, Upload, Eye, FileText, Plus, ClipboardList, GripVertical, AlertTriangle,
} from "lucide-react";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, useDroppable,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import YabanciKatilimciSahaDisiNotu from "../../Components/Firma/YabanciKatilimciSahaDisiNotu";
import DocumentPreviewModal from "../../Components/Common/DocumentPreviewModal";
import MainContext from "../../Components/Contexts/MainContext";
// #403: the live branch takes the logged-in company's id from the portal context (GET /portal/context).
import { usePortal } from "../../Components/Contexts/PortalContext";
import { getStandOverviewList } from "../../services/standOverviewService";
import { getProjectByStandId } from "../../services/projectService";
// WAS (until #403): getFirmaIletisimKisileri, MOCK_DECORATOR_FIRMA_ID, TEST_DECORATOR_FIRMA_ID,
import {
  getFirmaProfile, getFirmaDocuments, getFirmaBelgeleri, uploadFirmaDocument,
  getFirmaIletisimKisileri, MOCK_DECORATOR_FIRMA_ID,
} from "../../services/firmaProfileService";
import { getFirmaPersonelListesi } from "../../services/firmaPersonelListService";
import { kartDurumuMeta } from "../../services/kartDurumuService";
import { getStandDurumuOzet, SEVIYE_RENK } from "../../services/standDurumuService";
import { BORC_DURUMU_KATEGORILERI, BORC_DURUMU_STATUS } from "../../services/standTalepService";
// #319-#321 (2026-10-11): aktif arayüz setinin kısayolları (portal bağlamından).
import PortalKisayollari from "../../Components/Firma/PortalKisayollari";
import StandKodKutusu from "../../Components/Firma/StandKodKutusu";
// #432 (2026-10-16): "Firma Evrakları" satırı, eski kısayol düğmesinin kuralıyla (FIRMA_BELGELERI en az READ) çizilir.
import { enAz, MODUL, SEVIYE } from "../../services/portalService";
// #433 (2026-10-16): widget durumu kullanıcıya göre saklanır (bkz. anasayfaWidgetDurumu.js).
import {
  HIDDEN_WIDGETS_STORAGE_BASE, OPEN_PANELS_STORAGE_BASE,
  readUserStorage, writeUserStorage, reconcileOpenPanels, layoutDepolamaTabani, baslangicDuzeni,
} from "./anasayfaWidgetDurumu";
// #526 (2026-10-21): widget'lar ve okumalar, kenar menüyle aynı kuralla, setin modüllerine bağlanır (bkz. anasayfaModulleri.js).
// WAS (until #535): import { widgetGorunur, okumaPlani, modulOkunur, YUKLEME_SATIRI_MODULU } from "./anasayfaModulleri";
// #607 (2026-10-30): Yüklemeler satırlarının sırası ve "Widget Ekle" düğmesi Yabancı Katılımcı setinde farklıdır.
// WAS (until #607): import { widgetGorunur, okumaPlani, yuklemeSatiriGorunur } from "./anasayfaModulleri";
import { widgetGorunur, okumaPlani, yuklemeSatiriGorunur, yuklemeSatiriSirasi } from "./anasayfaModulleri";
// WAS (until #704): import { yabanciKatilimciMi } from "../../Components/Kisi/YabanciKatilimciParcalari";
import { sadePanelMi, gorunumSetKodu } from "../../services/sadePanel";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";

function getCurrentFirmaId() {
  try {
    const user = JSON.parse(localStorage.getItem("user"));
    return user?.firmaId ?? MOCK_DECORATOR_FIRMA_ID;
  } catch {
    return MOCK_DECORATOR_FIRMA_ID;
  }
}

// Canlı backend'e sahip test fuarları (298/299) — bkz. FirmaStandListPage.jsx/
// FirmaProjelerListPage.jsx/FirmaKartPage.jsx'teki aynı desen (dosya-yerel kopya).
// WAS (until #398): only the two seeded test fairs ran on the real backend, every other fair fell back to mock data:
// const LIVE_TEST_FUAR_IDS = [298, 299];
// const isLiveTestFuarId = (fuarId) => LIVE_TEST_FUAR_IDS.includes(Number(fuarId));
// #398: a full-simulation test creates a NEW fair, so every real fair id is live.
const isLiveTestFuarId = (fuarId) => Number(fuarId) > 0;

// stand_file 5 "Taahütnameler" alt-id 1=Statik/2=İSG/3=Elektrik — dosya-yerel kopya,
// aynısı FirmaStandListPage.jsx/FirmaProjelerListPage.jsx'te de var.
const COMMITMENT_SUBFILE_META = {
  1: { key: "statik", get label() { return i18n.t("portal:anasayfa.statikTaahutname"); } },
  2: { key: "isg", get label() { return i18n.t("portal:anasayfa.isgTaahutname"); } },
  3: { key: "elektrik", get label() { return i18n.t("portal:anasayfa.elektrikTaahutname"); } },
};
function deriveCommitmentsFromProject(bridged) {
  if (!bridged) return [];
  const files = (bridged.uploadedFiles || []).filter((f) => f.standFileId === 5 && COMMITMENT_SUBFILE_META[f.subStandFileId]);
  return Object.entries(COMMITMENT_SUBFILE_META).map(([subId, meta]) => {
    const matches = files.filter((f) => f.subStandFileId === Number(subId));
    const latest = [...matches].sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt))[0];
    return { key: meta.key, label: meta.label, status: latest?.approvalStatus ?? 0, fileId: latest?.id ?? null };
  });
}

// Canlı standId köprüsü (getStandOverviewList'in forceLive dalı proje alanlarını
// bilerek boş bırakır) — her stand için projectService.getProjectByStandId(forceLive:true)
// sonucunu requiredCount/approvedCount/constructionPermitGranted/projectDeclared/
// commitments'a çevirir. FirmaStandListPage.jsx/FirmaProjelerListPage.jsx'teki aynı
// isimli fonksiyonun dosya-yerel kopyası — standDurumu/standlarim/projeler/talepler
// widget'larının hepsi bu alanlara ihtiyaç duyar.
function mergeLiveProjectIntoStand(stand, bridged) {
  const requiredCount = bridged?.requiredCount ?? 0;
  const approvedCount = bridged?.approvedCount ?? 0;
  return {
    ...stand,
    requiredCount,
    approvedCount,
    constructionPermitGranted: bridged ? (requiredCount > 0 && approvedCount === requiredCount) : false,
    projectDeclared: bridged?.projeBeyani?.declared === true,
    commitments: deriveCommitmentsFromProject(bridged),
  };
}

const DOC_STATUS = {
  onayli:   { get label() { return i18n.t("portal:anasayfa.onayli"); },   color: "success" },
  eksik:    { get label() { return i18n.t("portal:anasayfa.eksik"); },    color: "danger" },
  yuklendi: { get label() { return i18n.t("portal:anasayfa.yuklendi"); }, color: "warning" },
};

// Anasayfa'daki katlanır panel widget'ları — kullanıcı bunları kaldırıp
// "Widget Ekle" menüsünden geri ekleyebilir (tercih localStorage'da saklanır).
//
// 2026-08-04 revizyonu (/revise-page): Kurulum/Söküm Kartı Durumu, İnşa İzin
// Durumu, Proje Evrakları Durumu ve Fuar Evrakları Durumu artık gerekli
// görülmediği için devre dışı bırakıldı (koddan silinmedi — aşağıda ve
// renderWidget() içinde yorum satırı olarak duruyorlar, gerekirse tek satır
// açılarak geri getirilebilir). Yerlerine Personeller/Standlarım/Projeler/
// Talepler/İletişim eklendi — Standlarım/Projeler/Talepler ilgili liste
// sayfasıyla AYNI renk mantığını kullanan küçük stand kutucukları (bkz.
// StandKodKutusu), Personeller eski Kurulum panelinin kişi satırlarını
// (retitled + "Sayfaya git") yeniden kullanıyor.
const WIDGET_DEFS = [
  // { key: "kurulum",     title: "Kurulum ve Söküm Kartı Durumu" },
  // { key: "insaIzin",    title: "İnşa İzin Durumu" },
  // { key: "projeEvrak",  title: "Proje Evrakları Durumu" },
  // { key: "fuarEvrak",   title: "Fuar Evrakları Durumu" },
  { key: "standDurumu", get title() { return i18n.t("portal:anasayfa.standDurumu"); } },
  { key: "yuklemeler",  get title() { return i18n.t("portal:anasayfa.yuklemeler"); } },
  { key: "personeller", get title() { return i18n.t("portal:anasayfa.personeller"); } },
  { key: "standlarim",  get title() { return i18n.t("portal:anasayfa.standlarim"); } },
  { key: "projeler",    get title() { return i18n.t("portal:anasayfa.projeler"); } },
  { key: "talepler",    get title() { return i18n.t("portal:anasayfa.talepler"); } },
  { key: "iletisim",    get title() { return i18n.t("portal:anasayfa.iletisim"); } },
];
// #433 (2026-10-16): widget durumu KULLANICIYA göre saklanır. WAS: tek, ortak anahtarlar — aynı tarayıcıda iki giriş birbirinin gizli
// widget'larını / sütun düzenini görüyordu:
// const HIDDEN_WIDGETS_STORAGE_KEY = "firmaAnasayfaHiddenWidgets";
// const LAYOUT_STORAGE_KEY = "firmaAnasayfaWidgetLayout";
// Eski ortak anahtarlar artık okunmaz (kullanıcılar düzenlerini bir kez kaybeder). Anahtarın sonuna GERÇEK giriş yapmış kullanıcının
// id'si eklenir (getAuthHeader'ın X-user-Id'sinin okuduğu 'userId'): personel önizlemesi (#411) bir firmayı gösterse bile personelin
// kendi id'si kullanılır, firma girişinin anahtarına yazılmaz. Giriş id'si yoksa hiçbir şey saklanmaz.
// (the helpers live in ./anasayfaWidgetDurumu.js, with their tests)

// Widget'ların sürükle-bırakla taşınabildiği 3 sütun ("widget-placeable" alanlar) —
// sütun id'leri widget key'leriyle çakışmaz, DndContext bunları ayırt eder.
const DEFAULT_LAYOUT = {
  col1: ["yuklemeler", "personeller"],
  col2: ["standDurumu", "standlarim"],
  col3: ["projeler", "talepler", "iletisim"],
};
// WAS (until #433): const LAYOUT_STORAGE_KEY = "firmaAnasayfaWidgetLayout"; — bkz. HIDDEN_WIDGETS_STORAGE_BASE notu yukarıda.

// localStorage'daki (veya WIDGET_DEFS değiştiyse eskimiş) layout'u onarır: bilinmeyen
// key'leri atar, eksik olanları col1'e ekler — her widget tam olarak bir kez bulunur.
function reconcileLayout(rawLayout) {
  const allKeys = WIDGET_DEFS.map((w) => w.key);
  const cols = ["col1", "col2", "col3"];
  const result = { col1: [], col2: [], col3: [] };
  const seen = new Set();
  cols.forEach((c) => {
    (rawLayout?.[c] || []).forEach((k) => {
      if (allKeys.includes(k) && !seen.has(k)) {
        result[c].push(k);
        seen.add(k);
      }
    });
  });
  allKeys.forEach((k) => {
    if (!seen.has(k)) result.col1.push(k);
  });
  return result;
}

function findWidgetColumn(layoutObj, id) {
  if (layoutObj[id]) return id;
  return Object.keys(layoutObj).find((col) => layoutObj[col].includes(id));
}

function SplitBar({ segments }) {
  const total = segments.reduce((sum, s) => sum + s.count, 0) || 1;
  return (
    <div className="mb-3">
      <div className="d-flex justify-content-between flex-wrap small mb-1">
        {segments.map((s) => (
          <span key={s.label} className={`text-${s.color} fw-semibold`}>
            {s.count} {s.label}
          </span>
        ))}
      </div>
      <div className="d-flex rounded-pill overflow-hidden" style={{ height: 8, background: "var(--bs-light)" }}>
        {segments.map((s) => (
          <div
            key={s.label}
            style={{ width: `${(s.count / total) * 100}%`, background: `var(--bs-${s.color})` }}
          />
        ))}
      </div>
    </div>
  );
}

function PanelCard({ title, isOpen, onToggle, onRemove, dragHandleProps, children, footer, tour = "anasayfa-widget" }) {
  const { t } = useTranslation("portal");
  return (
    <Card className="mb-4" data-tour={tour}>
      <CardHeader
        role="button"
        tabIndex={0}
        onClick={onToggle}
        className="bg-danger text-white d-flex justify-content-between align-items-center"
      >
        <div className="d-flex align-items-center gap-2">
          {dragHandleProps && (
            <span
              {...dragHandleProps}
              onClick={(e) => e.stopPropagation()}
              title={t("anasayfa.surukleyerekTasi")}
              className="d-flex align-items-center"
              style={{ cursor: "grab", touchAction: "none" }}
            >
              <GripVertical size={14} />
            </span>
          )}
          <span className="fw-semibold small text-uppercase">{title}</span>
        </div>
        <div className="d-flex align-items-center gap-2">
          {onRemove && (
            <button
              type="button"
              className="btn btn-sm p-0 border-0 bg-transparent text-white d-flex align-items-center"
              title={t("anasayfa.widgetIKaldir")}
              onClick={(e) => { e.stopPropagation(); onRemove(); }}
            >
              <X size={14} />
            </button>
          )}
          {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </CardHeader>
      <Collapse isOpen={isOpen}>
        <CardBody>{children}</CardBody>
        {footer && <div className="text-center pb-3">{footer}</div>}
      </Collapse>
    </Card>
  );
}

// Sütun (Sol/Orta/Sağ) — boşken bile geçerli bir bırakma alanı olsun diye useDroppable,
// içindeki widget'ların sırasını izlemek için de SortableContext birlikte kullanılıyor.
function WidgetColumn({ id, itemIds, children }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      style={{
        minHeight: 60,
        borderRadius: 8,
        outline: isOver ? "2px dashed var(--bs-danger)" : "2px dashed transparent",
        outlineOffset: 4,
        transition: "outline-color 0.15s",
      }}
    >
      <SortableContext id={id} items={itemIds} strategy={verticalListSortingStrategy}>
        {children}
      </SortableContext>
    </div>
  );
}

// Tek bir widget'ı sürüklenebilir yapar — sürükleme kollarını (attributes/listeners)
// PanelCard'ın dragHandleProps'una clone ederek geçirir (bkz. InspectionHierarchyPage
// SortableRow ile aynı desen).
function SortableWidget({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };
  return (
    <div ref={setNodeRef} style={style}>
      {React.cloneElement(children, { dragHandleProps: { ...attributes, ...listeners } })}
    </div>
  );
}

function PersonnelRow({ person, onView }) {
  const { t } = useTranslation("portal");
  return (
    <div className="d-flex align-items-center justify-content-between border rounded p-2 mb-2">
      <div className="d-flex align-items-center gap-2">
        <div
          className="rounded-circle bg-light d-flex align-items-center justify-content-center flex-shrink-0"
          style={{ width: 42, height: 42 }}
        >
          <User size={20} className="text-muted" />
        </div>
        <div>
          <div className="fw-semibold small">{person.name}</div>
          <div className="text-muted" style={{ fontSize: "0.75rem" }}>{person.phone}</div>
        </div>
      </div>
      <div className="d-flex align-items-center gap-2">
        {/* 2026-09-15: iki durumlu rozet üç duruma çıktı (sarı = evrak tamam, kart alamaz) —
            bkz. kartDurumuService.js. Eski hali:
        <Badge color={person.cardEligible ? "success" : "danger"} pill className="d-flex align-items-center">
          {person.cardEligible ? <Check size={12} /> : <X size={12} />}
        </Badge>
        */}
        {(() => {
          const meta = kartDurumuMeta(person, { portal: true });
          return (
            <Badge color={meta.color} pill className="d-flex align-items-center" title={meta.label}>
              {meta.color === "success" ? <Check size={12} />
                : meta.color === "warning" ? <AlertTriangle size={12} />
                : <X size={12} />}
            </Badge>
          );
        })()}
        <Button color="info" outline size="sm" title={t("anasayfa.personelDetayi")} onClick={onView}>
          <ArrowRight size={14} />
        </Button>
      </div>
    </div>
  );
}

// "İletişim" widget'ı — dekoratörün ulaşabileceği Saka Grup tarafı kişileri
// (bkz. firmaProfileService.getFirmaIletisimKisileri). Firma Kartı'ndaki firmanın
// KENDİ yetkilileri/kullanıcılarından ayrı bir liste, o yüzden ayrı ve daha küçük
// bir satır bileşeni — tıklanabilir tel:/mailto: bağlantıları.
function IletisimRow({ kisi }) {
  return (
    <div className="border rounded p-2 mb-2">
      <div className="d-flex align-items-center gap-2 mb-1">
        <div
          className="rounded-circle bg-light d-flex align-items-center justify-content-center flex-shrink-0"
          style={{ width: 32, height: 32 }}
        >
          <User size={16} className="text-muted" />
        </div>
        <div>
          <div className="fw-semibold small">{kisi.adSoyad}</div>
          <div className="text-muted" style={{ fontSize: "0.7rem" }}>{kisi.role}</div>
        </div>
      </div>
      {/* #538: telefon ya da e-postadan biri boş olabilir (en az biri dolu), yalnızca dolu olan basılır. */}
      <div className="d-flex flex-column gap-1 ps-1" style={{ fontSize: "0.75rem" }}>
        {kisi.telefon && (
          <a href={`tel:${kisi.telefon}`} className="text-muted text-decoration-none d-flex align-items-center gap-1">
            <Phone size={12} />{kisi.telefon}
          </a>
        )}
        {kisi.eposta && (
          <a href={`mailto:${kisi.eposta}`} className="text-muted text-decoration-none d-flex align-items-center gap-1">
            <Mail size={12} />{kisi.eposta}
          </a>
        )}
      </div>
    </div>
  );
}

// #487: the stand-code chips (StandChip outlined 40 px, MiniStandChip filled 52 px) are the shared
// Components/Firma/StandKodKutusu.jsx now — same box as Standlarım and Projeler.

function ClientStandRow({ stand, badge, onView }) {
  const { t } = useTranslation("portal");
  return (
    <div className="d-flex align-items-center justify-content-between border rounded p-2 mb-2">
      <div className="d-flex align-items-center gap-2">
        <StandKodKutusu stand={stand} outline={badge.color} size={40} />
        <div>
          <div className="fw-semibold small">{stand.firmaName}</div>
          <div className="text-muted" style={{ fontSize: "0.75rem" }}>{stand.salonName} — {stand.standName}</div>
        </div>
      </div>
      <div className="d-flex align-items-center gap-2">
        {/* badge.hex: "Stand Durumu" widget'ındaki en üst kademeyi ("Tüm Kontroller
            Tamam") normal success yeşilinden ayırmak için opsiyonel inline arkaplan —
            reactstrap Badge'in sabit renk paletinde açık yeşil bir ton yok. */}
        <Badge color={badge.color} style={badge.hex ? { background: badge.hex } : undefined}>{badge.label}</Badge>
        <Button color="info" outline size="sm" title={t("common:goruntule")} onClick={onView}>
          <Eye size={14} />
        </Button>
      </div>
    </div>
  );
}

// "Stand Durumu" widget'ı için özet -> rozet dönüşümü (bkz. standDurumuService.js'teki
// `seviye`). Özet henüz yüklenmediyse (Promise.all tamamlanmadan) nötr bir "Yükleniyor"
// rozeti gösterilir.
function standDurumuBadgeMeta(ozet) {
  if (!ozet) return { label: i18n.t("portal:anasayfa.yukleniyor"), color: "secondary" };
  if (ozet.seviye === "projeYok") return { label: i18n.t("portal:anasayfa.projeYok"), color: "secondary" };
  if (ozet.seviye === "sorunlu") return { label: i18n.t("portal:anasayfa.sorunlu"), color: "danger" };
  if (ozet.seviye === "tam") return { label: i18n.t("portal:anasayfa.tumKontrollerTamam"), color: "success", hex: "#75dd93" };
  return { label: i18n.t("portal:anasayfa.durumuUygun"), color: "success" };
}

function UploadLinkRow({ label, icon: Icon, onClick }) {
  const { t } = useTranslation("portal");
  return (
    <div className="d-flex align-items-center justify-content-between border rounded p-2 mb-2">
      <div className="d-flex align-items-center gap-2">
        <Icon size={18} className="text-muted flex-shrink-0" />
        <div className="fw-semibold small">{label}</div>
      </div>
      <Button color="info" outline size="sm" title={t("common:goruntule")} onClick={onClick}>
        <ArrowRight size={14} />
      </Button>
    </div>
  );
}

// Sadece aşağıda yorum satırı olan "projeEvrak" widget case'i tarafından kullanılıyordu.
// function projeEvrakBadge(stand) {
//   if (stand.requiredCount === 0) return { label: "—", color: "secondary" };
//   if (stand.approvedCount === 0) return { label: "Başlanmadı", color: "danger" };
//   if (stand.approvedCount < stand.requiredCount) return { label: "Devam Ediyor", color: "warning" };
//   return { label: "Tamamlandı", color: "success" };
// }

// Yeni Anasayfa widget'ları (Standlarım/Projeler/Talepler) için ortak küçük stand
// kutucuğu (52 px, dolu arkaplan) — #487'den beri paylaşılan StandKodKutusu: stand kodu tek
// satırda (eskiden "S1" salon kısaltması + kod iki satırdı; salon artık tooltip'te, tam stand
// adı "11.A10" içinde), tıklanınca ilgili liste sayfasındaki "Detaylar" hedefiyle AYNI yere
// gider (bkz. her widget'ın onChipClick'i). Aşağıdaki MiniStandChipGrid onu 52 px basar.

function MiniStandChipGrid({ stands, colorFn, onChipClick, emptyText }) {
  if (stands.length === 0) {
    return <div className="text-muted small">{emptyText}</div>;
  }
  return (
    <div className="d-flex flex-wrap gap-2">
      {stands.map((s) => {
        const { color, title } = colorFn(s);
        return <StandKodKutusu key={s.id} stand={s} color={color} size={52} hint={title} onClick={() => onChipClick(s)} />;
      })}
    </div>
  );
}

// "Standlarım" widget'ı — kutucuk rengi standDurumuOzetMap'teki AYNI `seviye` ->
// SEVIYE_RENK eşlemesi (standDurumuBadgeMeta'nın hex kaynağıyla aynı, bkz.
// standDurumuService.js), Standlarım liste sayfasındaki (FirmaStandListPage.jsx)
// StandThumbnail ile birebir aynı kaynak.
const STANDLARIM_SEVIYE_LABEL = {
  get projeYok() { return i18n.t("portal:anasayfa.projeYok"); }, get sorunlu() { return i18n.t("portal:anasayfa.sorunlu"); }, get tamamlandi() { return i18n.t("portal:anasayfa.durumuUygun"); }, get tam() { return i18n.t("portal:anasayfa.tumKontrollerTamam"); },
};
function standlarimColorFor(stand, standDurumuOzetMap) {
  const seviye = standDurumuOzetMap[stand.id]?.seviye;
  const label = seviye ? STANDLARIM_SEVIYE_LABEL[seviye] ?? seviye : i18n.t("portal:anasayfa.yukleniyor");
  return {
    color: seviye ? SEVIYE_RENK[seviye] : SEVIYE_RENK.projeYok,
    title: `${stand.standName ?? stand.standCode} — ${label}`,
  };
}

// "Projeler" widget'ı — kutucuk rengi İnşa İzni durumunu taşır, Projeler liste
// sayfasındaki (FirmaProjelerListPage.jsx) StandThumbnail ile AYNI izinVar formülü
// (constructionPermitGranted varsa onu kullan, yoksa requiredCount/approvedCount'tan
// türet). Proje bilgisi henüz girilmemiş standlar (projectDeclared: false) o
// sayfada uyarı kartıyla ayrı gösteriliyor — burada turuncu kutucukla işaretlenir.
function projelerColorFor(stand) {
  if (stand.projectDeclared === false) {
    return { color: "#f7b84b", title: i18n.t("portal:anasayfa.projeBeyaniBekleniyor", { v: stand.standName ?? stand.standCode }) };
  }
  const izinVar = stand.constructionPermitGranted ?? (stand.requiredCount > 0 && stand.approvedCount === stand.requiredCount);
  return izinVar
    ? { color: "#0ab39c", title: i18n.t("portal:anasayfa.insaIzniVerildi", { v: stand.standName ?? stand.standCode }) }
    : { color: "#f06548", title: i18n.t("portal:anasayfa.insaIzniVerilmedi", { v: stand.standName ?? stand.standCode }) };
}

// "Talepler" widget'ı — kutucuk rengi Borç Durumu'nu taşır, Talepler liste
// sayfasındaki (FirmaTaleplerListPage.jsx) talepBorcRenk ile AYNI mantık: ilgili
// (Alan/Teknik/Ekipman) kalemlerden biri "bekliyor"sa kırmızı, aksi halde teal.
// borcItems standDurumuOzetMap'ten geliyor (getStandDurumuOzet zaten getBorcDurumu'nu
// çekiyor) — ayrı bir fetch gerekmiyor.
function talepColorFor(stand, standDurumuOzetMap) {
  const borcItems = standDurumuOzetMap[stand.id]?.borcItems;
  const label = `${stand.standName ?? stand.standCode}`;
  if (!borcItems) return { color: "#878a99", title: i18n.t("portal:anasayfa.yukleniyor2", { label }) };
  const relevant = borcItems.filter((i) => BORC_DURUMU_KATEGORILERI.some((c) => c.key === i.categoryKey));
  if (relevant.length === 0) return { color: "#878a99", title: i18n.t("portal:anasayfa.borcKaydiYok", { label }) };
  const bekliyor = relevant.some((i) => i.status === "bekliyor");
  return bekliyor
    ? { color: BORC_DURUMU_STATUS.bekliyor.hex, title: i18n.t("portal:anasayfa.borcBekliyor", { label }) }
    : { color: BORC_DURUMU_STATUS.odendi.hex, title: i18n.t("portal:anasayfa.borcYok", { label }) };
}

export default function FirmaAnasayfaListPage() {
  const { t: tx } = useTranslation("portal");
  const navigate = useNavigate();
  const { selectedFuarId } = useContext(MainContext);
  const portal = usePortal();
  const isLiveTestFuar = isLiveTestFuarId(selectedFuarId);
  // WAS (until #403): const firmaId = isLiveTestFuar ? TEST_DECORATOR_FIRMA_ID : getCurrentFirmaId();
  // null while /portal/context loads: the effects below wait for it instead of asking for firma "null".
  const firmaId = isLiveTestFuar ? (portal?.firmaId ?? null) : getCurrentFirmaId();
  // #526: aktif setin modül seviyeleri. Set yoksa (firma bu fuarda kayıtlı değil) her modül kapalı sayılır.
  const seviyeOf = (modul) => (portal?.aktif ? portal.seviye(modul) : SEVIYE.NONE);
  // #527: aktif setin kodu — bazı setlerde (Yabancı Katılımcı) yalnızca birkaç widget açık, bkz. anasayfaModulleri.SET_WIDGETLARI.
  // #704: the LAYOUT's code: the Yabancı Katılımcı's while Sade panel modu is ON (a Yerli company keeps its own set code, its own saved
  // layout comes back when the switch goes OFF). WAS (until #704): const setKodu = portal?.aktif?.kod ?? null;
  const setKodu = gorunumSetKodu(portal);
  // The effect below depends on these two booleans (not on `portal`), so a role switch that changes them refetches.
  // WAS (until #527): okumaPlani(seviyeOf)
  const { standlar: standlariOku, personel: personelOku } = okumaPlani(seviyeOf, setKodu);

  const [profile, setProfile] = useState(null);
  const [personnel, setPersonnel] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [firmaBelgeleri, setFirmaBelgeleri] = useState({});
  // "İletişim" widget'ı — dekoratörün ulaşabileceği Saka Grup tarafı kişileri
  // (bkz. firmaProfileService.getFirmaIletisimKisileri).
  const [iletisimKisileri, setIletisimKisileri] = useState([]);
  const [clientStands, setClientStands] = useState([]);
  // Her müşteri standı için standDurumuService.js özeti — key: stand.id. clientStands
  // yüklendikten sonra ayrı bir effect'te Promise.all ile toplu çekilir (bkz. aynı
  // desen FirmaStandListPage.jsx'teki per-stand teknik talep fetch'i).
  const [standDurumuOzetMap, setStandDurumuOzetMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [infoMsg, setInfoMsg] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  // WAS (until #433): useState({ standDurumu: true, yuklemeler: true, personeller: true, standlarim: true, projeler: true,
  //   talepler: true, iletisim: true }) — hepsi açık, hiçbir şey saklanmıyordu, her ziyarette sıfırlanıyordu
  //   (kurulum: true, insaIzin: true, projeEvrak: true, fuarEvrak: true devre dışı widget'lardı).
  const [openPanels, setOpenPanels] = useState(() => reconcileOpenPanels(readUserStorage(OPEN_PANELS_STORAGE_BASE)));
  // WAS (until #433): const raw = localStorage.getItem(HIDDEN_WIDGETS_STORAGE_KEY); return raw ? JSON.parse(raw) : [];
  const [hiddenWidgets, setHiddenWidgets] = useState(() => {
    const saved = readUserStorage(HIDDEN_WIDGETS_STORAGE_BASE);
    return Array.isArray(saved) ? saved : [];
  });
  const [addWidgetOpen, setAddWidgetOpen] = useState(false);
  // WAS (until #433): const raw = localStorage.getItem(LAYOUT_STORAGE_KEY); reconcileLayout(raw ? JSON.parse(raw) : DEFAULT_LAYOUT)
  // WAS (until #535): const [layout, setLayout] = useState(() => reconcileLayout(readUserStorage(LAYOUT_STORAGE_BASE) ?? DEFAULT_LAYOUT));
  // #535: a set with its own default layout (Yabancı Katılımcı: Yüklemeler in the middle column) keeps its layout under its own storage key.
  // The set arrives after the first render (portal context), so the layout is held together with the key it belongs to and re-read
  // when that key changes (state derived during render, no effect).
  const layoutBase = layoutDepolamaTabani(setKodu);
  const [layoutSlot, setLayoutSlot] = useState(() => ({ base: layoutBase, layout: reconcileLayout(baslangicDuzeni(setKodu, DEFAULT_LAYOUT)) }));
  if (layoutSlot.base !== layoutBase) {
    setLayoutSlot({ base: layoutBase, layout: reconcileLayout(baslangicDuzeni(setKodu, DEFAULT_LAYOUT)) });
  }
  const layout = layoutSlot.layout;
  const setLayout = useCallback((guncelle) => setLayoutSlot((s) => ({ ...s, layout: guncelle(s.layout) })), []);

  useEffect(() => { document.title = i18n.t("portal:anasayfa.anaSayfaFds"); }, []);

  // WAS (until #433): localStorage.setItem(HIDDEN_WIDGETS_STORAGE_KEY, JSON.stringify(hiddenWidgets)) / (LAYOUT_STORAGE_KEY, ...layout)
  useEffect(() => {
    writeUserStorage(HIDDEN_WIDGETS_STORAGE_BASE, hiddenWidgets);
  }, [hiddenWidgets]);

  // WAS (until #535): writeUserStorage(LAYOUT_STORAGE_BASE, layout) on [layout]
  useEffect(() => {
    writeUserStorage(layoutSlot.base, layoutSlot.layout);
  }, [layoutSlot]);

  useEffect(() => {
    writeUserStorage(OPEN_PANELS_STORAGE_BASE, openPanels);
  }, [openPanels]);

  useEffect(() => {
    // WAS (until #403): if (!selectedFuarId) return;
    if (!selectedFuarId || !firmaId) return;
    let ignore = false;

    async function run() {
      setLoading(true);
      setError(null);
      try {
        // #526: yalnızca firma PROFİLİ zorunlu (sayfa onsuz çizilmez). Diğer okumalar sayfayı düşürmez: setin ulaşamadığı modülün
        // okuması hiç yapılmaz (GET /standTalep/list STANDLAR en az READ ister, yoksa backend sert 403 verir), yapılıp da
        // reddedilen okuma boş değerle devam eder ve ilk hatanın mesajı sayfanın üstündeki uyarıda görünür.
        // WAS (until #526): hepsi tek Promise.all'da, biri reddedilince profil de düşüyor ve sayfa yalnızca 403 uyarısı gösteriyordu.
        let okumaHatasi = null;
        const opsiyonel = (istek, varsayilan) => istek.catch((e) => {
          okumaHatasi = okumaHatasi ?? e.message;
          return varsayilan;
        });
        const [profileData, personnelData, documentsData, firmaBelgeleriData, iletisimData, standsData] = await Promise.all([
          getFirmaProfile(firmaId, { forceLive: isLiveTestFuar, fuarId: selectedFuarId }),
          personelOku
            ? opsiyonel(getFirmaPersonelListesi(
              { decoratorFirmaId: firmaId, fuarId: selectedFuarId, limit: 100 },
              { forceLive: isLiveTestFuar }
            ), { records: [] })
            : { records: [] },
          opsiyonel(getFirmaDocuments(firmaId, { forceLive: isLiveTestFuar, fuarId: selectedFuarId }), []),
          opsiyonel(getFirmaBelgeleri(firmaId, { forceLive: isLiveTestFuar }), {}),
          // #538: canlı (backend #537): firmanın, fuarın ve genel iletişim kişileri; seçili fuar yoksa ya da firma bilinmiyorsa istek atılmaz.
          // WAS (until #538): opsiyonel(getFirmaIletisimKisileri(firmaId), []) — her zaman sabit satır dönen mock.
          isLiveTestFuar && firmaId != null
            ? opsiyonel(getFirmaIletisimKisileri(firmaId, { fuarId: selectedFuarId }), [])
            : [],
          // WAS (until #425): getStandOverviewList({ fuarId: selectedFuarId, decoratorFirmaId: firmaId, limit: 100, forceLive: isLiveTestFuar }),
          // Canlı: firmanın HER rolde olduğu standlar (backend #423) — Katılımcı girişi de standlarını görür.
          // (Yukarıdaki getFirmaPersonelListesi decoratorFirmaId'de KALIR: o stand değil personel roster'ı.)
          standlariOku
            ? opsiyonel(getStandOverviewList({
              fuarId: selectedFuarId,
              ...(isLiveTestFuar ? { firmaId } : { decoratorFirmaId: firmaId }),
              limit: 100,
              forceLive: isLiveTestFuar,
            }), { records: [] })
            : { records: [] },
        ]);
        if (ignore) return;
        if (okumaHatasi) setError(okumaHatasi);
        setProfile(profileData);
        setPersonnel(personnelData.records || []);
        setDocuments(documentsData);
        setFirmaBelgeleri(firmaBelgeleriData);
        setIletisimKisileri(iletisimData);

        // Proje köprüsü — getStandOverviewList'in canlı dalı proje alanlarını (bkz.
        // dosya başı mergeLiveProjectIntoStand) bilerek boş bırakıyor, burada sayfa
        // seviyesinde her stand için ayrıca çözülüyor (Standlarım/Projeler/Talepler/
        // Stand Durumu widget'larının hepsi bu alanlara ihtiyaç duyar).
        let stands = standsData.records || [];
        if (isLiveTestFuar && stands.length > 0) {
          const bridged = await Promise.all(
            stands.map((s) => getProjectByStandId(s.standId, { forceLive: true }).catch(() => null))
          );
          stands = stands.map((s, i) => mergeLiveProjectIntoStand(s, bridged[i]));
        }
        if (!ignore) setClientStands(stands);
      } catch (e) {
        if (!ignore) setError(e.message);
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    run();
    return () => { ignore = true; };
    // WAS (until #526): }, [selectedFuarId, firmaId, isLiveTestFuar, reloadToken]);
    // #526: the two reads the active set may close (a role switch can change them).
  }, [selectedFuarId, firmaId, isLiveTestFuar, reloadToken, standlariOku, personelOku]);

  // "Stand Durumu" widget'ı — clientStands + firma evrak verisi hazır olunca her
  // stand için özet hesaplanır (bkz. standDurumuService.getStandDurumuOzet). Sayfa
  // seviyesinde bir kez çekilen profile/documents/firmaBelgeleri, her stand için
  // tekrar tekrar istenmesin diye tüm çağrılara aynen geçilir (firma evrakları
  // stand bazlı değil, dekoratör firması bazlı bir veridir).
  useEffect(() => {
    if (clientStands.length === 0) return;
    let ignore = false;

    async function run() {
      // WAS (until #526): await getStandDurumuOzet(...) with no catch — one refused stand (a 403 on its talep reads) rejected the whole
      // Promise.all, left the map empty and was an unhandled rejection.
      // #526: a stand whose summary fails is left out (its box stays neutral) and the others still get theirs.
      const entries = await Promise.all(
        clientStands.map(async (s) => [
          s.id,
          await getStandDurumuOzet(s, { profile, documents, firmaBelgeleri }, { forceLive: isLiveTestFuar }).catch(() => null),
        ])
      );
      if (!ignore) setStandDurumuOzetMap(Object.fromEntries(entries.filter(([, ozet]) => ozet)));
    }

    run();
    return () => { ignore = true; };
  }, [clientStands, profile, documents, firmaBelgeleri, isLiveTestFuar]);

  function showInfo(msg) {
    setInfoMsg(msg);
    setTimeout(() => setInfoMsg(null), 3500);
  }

  function togglePanel(key) {
    setOpenPanels((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  function hideWidget(key) {
    setHiddenWidgets((prev) => (prev.includes(key) ? prev : [...prev, key]));
  }

  function showWidget(key) {
    setHiddenWidgets((prev) => prev.filter((k) => k !== key));
  }

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Sürükleme sırasında farklı bir sütunun üzerine gelindiğinde widget'ı o an o
  // sütuna taşır (canlı önizleme) — aynı sütun içindeki nihai sıralama handleDragEnd'de.
  const handleDragOver = useCallback((event) => {
    const { active, over } = event;
    if (!over) return;
    const activeId = String(active.id);
    const overId = String(over.id);
    if (activeId === overId) return;

    setLayout((prev) => {
      const activeCol = findWidgetColumn(prev, activeId);
      const overCol = findWidgetColumn(prev, overId);
      if (!activeCol || !overCol || activeCol === overCol) return prev;

      const activeItems = prev[activeCol];
      const overItems = prev[overCol];
      const overIndex = overItems.indexOf(overId);
      const insertAt = overIndex === -1 ? overItems.length : overIndex;

      return {
        ...prev,
        [activeCol]: activeItems.filter((k) => k !== activeId),
        [overCol]: [...overItems.slice(0, insertAt), activeId, ...overItems.slice(insertAt)],
      };
    });
  }, [setLayout]);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over) return;
    const activeId = String(active.id);
    const overId = String(over.id);
    if (activeId === overId) return;

    setLayout((prev) => {
      const activeCol = findWidgetColumn(prev, activeId);
      const overCol = findWidgetColumn(prev, overId);
      if (!activeCol || !overCol || activeCol !== overCol) return prev;
      const oldIndex = prev[activeCol].indexOf(activeId);
      const newIndex = prev[overCol].indexOf(overId);
      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return prev;
      return { ...prev, [activeCol]: arrayMove(prev[activeCol], oldIndex, newIndex) };
    });
  }, [setLayout]);

  async function handleUploadDoc(docKey, file) {
    setUploadingDoc(docKey);
    setError(null);
    try {
      await uploadFirmaDocument(firmaId, docKey, file);
      setReloadToken((t) => t + 1);
    } catch (e) {
      setError(e.message);
    } finally {
      setUploadingDoc(null);
    }
  }

  // 2026-09-15: sayaç üç duruma bölündü — bkz. kartDurumuService.js. Eski hali:
  // const eligibleCount = personnel.filter((p) => p.cardEligible).length;
  // const notEligibleCount = personnel.length - eligibleCount;
  // #374: kartDurumuMeta reads the row's `kkd` — a green row whose blocking PPE item is missing counts in the yellow segment, like its pill.
  const eligibleCount = personnel.filter((p) => kartDurumuMeta(p).key === "KART_ALABILIR").length;
  const evrakTamamCount = personnel.filter((p) => ["EVRAK_TAMAM_KART_ALAMAZ", "ALANDA_KART_ALABILIR"].includes(kartDurumuMeta(p).key)).length;
  // Kırmızı: evrak eksik (mock modunda eski "Kart Alamaz" da buraya düşer).
  const notEligibleCount = personnel.length - eligibleCount - evrakTamamCount;

  // Sadece aşağıda yorum satırı olan "insaIzin" widget case'i tarafından kullanılıyordu.
  // const izinVerildiCount = clientStands.filter((s) => s.requiredCount > 0 && s.approvedCount === s.requiredCount).length;
  // const izinVerilmediCount = clientStands.length - izinVerildiCount;

  // Sadece aşağıda yorum satırı olan "projeEvrak" widget case'i tarafından kullanılıyordu.
  // const projeCounts = clientStands.reduce(
  //   (acc, s) => {
  //     const b = projeEvrakBadge(s);
  //     if (b.label === "Tamamlandı") acc.tamamlandi += 1;
  //     else if (b.label === "Devam Ediyor") acc.devamEdiyor += 1;
  //     else if (b.label === "Başlanmadı") acc.baslanmadi += 1;
  //     return acc;
  //   },
  //   { tamamlandi: 0, devamEdiyor: 0, baslanmadi: 0 }
  // );

  // Widget key'ini gerçek PanelCard'a çevirir — 3 sütun da aynı fonksiyonu kullanarak
  // hangi widget'ın nerede olduğunu sadece `layout` state'inden okur (bkz. handleDragEnd/Over).
  function renderWidget(key) {
    switch (key) {
      case "yuklemeler":
        return (
          <PanelCard
            tour="anasayfa-yuklemeler"
            title={tx("anasayfa.yuklemeler")}
            isOpen={openPanels.yuklemeler}
            onToggle={() => togglePanel("yuklemeler")}
            onRemove={() => hideWidget("yuklemeler")}
          >
            {/* WAS (until #526): the three rows were always drawn; each now follows its page's module, so a set without Talepler
                (or Personel, Projeler) has no dead link here. */}
            {/* WAS (until #535): modulOkunur(seviyeOf, YUKLEME_SATIRI_MODULU.<satır>) — the set's own hidden rows (Yabancı Katılımcı: no Proje Yüklemeleri) count too. */}
            {/* WAS (until #607): the four rows were written out here in a fixed order (personel, proje, talep, firma);
                yuklemeSatiriSirasi(setKodu) now gives the order (Yabancı Katılımcı: Firma Evrakları and Personel Yüklemeleri swapped). */}
            {yuklemeSatiriSirasi(setKodu).map((satir) => {
              switch (satir) {
                case "personel":
                  return yuklemeSatiriGorunur("personel", seviyeOf, setKodu) && (
                    <UploadLinkRow key={satir} label={tx("anasayfa.personelYuklemeleri")} icon={User} onClick={() => navigate("/Firma/Personel")} />
                  );
                case "proje":
                  return yuklemeSatiriGorunur("proje", seviyeOf, setKodu) && (
                    <UploadLinkRow key={satir} label={tx("anasayfa.projeYuklemeleri")} icon={FileText} onClick={() => navigate("/Firma/Projeler")} />
                  );
                case "talep":
                  return yuklemeSatiriGorunur("talep", seviyeOf, setKodu) && (
                    <UploadLinkRow key={satir} label={tx("anasayfa.talepYuklemeleri")} icon={ClipboardList} onClick={() => navigate("/Firma/Talepler")} />
                  );
                case "firma":
                  /* #432: üstteki kısayol düğmesinin yerine; eski düğmenin kuralıyla (FIRMA_BELGELERI en az READ) görünür. */
                  return portal?.aktif && enAz(portal.seviye(MODUL.FIRMA_BELGELERI), SEVIYE.READ) && (
                    <UploadLinkRow key={satir} label={tx("anasayfa.firmaEvraklari")} icon={Building2} onClick={() => navigate("/Firma/FirmaKart?tab=evraklar")} />
                  );
                default:
                  return null;
              }
            })}
          </PanelCard>
        );
      /* 2026-08-04: Kurulum/Söküm Kartı Durumu, İnşa İzin Durumu, Proje Evrakları
         Durumu ve Fuar Evrakları Durumu widget'ları artık gerekli görülmediği için
         devre dışı — WIDGET_DEFS'te de yorum satırı, bkz. dosya başı notu. Kod
         silinmedi, gerekirse case'ler + WIDGET_DEFS girdileri açılarak geri getirilebilir.
      case "kurulum":
        return (
          <PanelCard
            title="Kurulum ve Söküm Kartı Durumu"
            isOpen={openPanels.kurulum}
            onToggle={() => togglePanel("kurulum")}
            onRemove={() => hideWidget("kurulum")}
            footer={
              <Button color="link" size="sm" onClick={() => navigate("/Firma/Personel")}>
                Tüm Personeller
              </Button>
            }
          >
            <SplitBar segments={[
              { label: "Kart Alamaz", count: notEligibleCount, color: "danger" },
              { label: "Kart Alabilir", count: eligibleCount, color: "success" },
            ]} />
            {personnel.length === 0 ? (
              <div className="text-muted small">Personel bulunamadı.</div>
            ) : (
              personnel.map((p) => (
                <PersonnelRow key={p.id} person={p} onView={() => showInfo("Personel detayı yakında eklenecek.")} />
              ))
            )}
          </PanelCard>
        );
      case "insaIzin":
        return (
          <PanelCard
            title="İnşa İzin Durumu"
            isOpen={openPanels.insaIzin}
            onToggle={() => togglePanel("insaIzin")}
            onRemove={() => hideWidget("insaIzin")}
            footer={
              <Button color="link" size="sm" onClick={() => navigate("/Firma/Standlar")}>
                Tümünü Gör
              </Button>
            }
          >
            <SplitBar segments={[
              { label: "Verildi", count: izinVerildiCount, color: "success" },
              { label: "Verilmedi", count: izinVerilmediCount, color: "danger" },
            ]} />
            {clientStands.length === 0 ? (
              <div className="text-muted small">Bu fuarda size bağlı müşteri standı bulunamadı.</div>
            ) : (
              clientStands.map((s) => {
                const izinVar = s.requiredCount > 0 && s.approvedCount === s.requiredCount;
                const badge = izinVar ? { label: "Verildi", color: "success" } : { label: "Verilmedi", color: "danger" };
                return (
                  <ClientStandRow
                    key={s.id}
                    stand={s}
                    badge={badge}
                    onView={() => navigate(`/Firma/Anasayfa/${s.id}`)}
                  />
                );
              })
            )}
          </PanelCard>
        );
      case "projeEvrak":
        return (
          <PanelCard
            title="Proje Evrakları Durumu"
            isOpen={openPanels.projeEvrak}
            onToggle={() => togglePanel("projeEvrak")}
            onRemove={() => hideWidget("projeEvrak")}
            footer={
              <Button color="link" size="sm" onClick={() => navigate("/Firma/Standlar")}>
                Tümünü Gör
              </Button>
            }
          >
            <SplitBar segments={[
              { label: "Tamamlandı", count: projeCounts.tamamlandi, color: "success" },
              { label: "Devam Ediyor", count: projeCounts.devamEdiyor, color: "warning" },
              { label: "Başlanmadı", count: projeCounts.baslanmadi, color: "danger" },
            ]} />
            {clientStands.length === 0 ? (
              <div className="text-muted small">Bu fuarda size bağlı müşteri standı bulunamadı.</div>
            ) : (
              clientStands.map((s) => (
                <ClientStandRow
                  key={s.id}
                  stand={s}
                  badge={projeEvrakBadge(s)}
                  onView={() => navigate(`/Firma/Anasayfa/${s.id}`)}
                />
              ))
            )}
          </PanelCard>
        );
      case "fuarEvrak":
        return (
          <PanelCard
            title="Fuar Evrakları Durumu"
            isOpen={openPanels.fuarEvrak}
            onToggle={() => togglePanel("fuarEvrak")}
            onRemove={() => hideWidget("fuarEvrak")}
          >
            <SplitBar segments={Object.entries(DOC_STATUS).map(([key, meta]) => ({
              label: meta.label,
              color: meta.color,
              count: documents.filter((d) => d.status === key).length,
            }))} />
            {documents.map((d) => {
              const info = DOC_STATUS[d.status] ?? DOC_STATUS.eksik;
              const inputId = `firma-doc-${d.key}`;
              const hasFile = d.status !== "eksik";
              return (
                <div key={d.key} className="d-flex align-items-center justify-content-between border rounded p-2 mb-2">
                  <div
                    className="d-flex align-items-center gap-2"
                    role={hasFile ? "button" : undefined}
                    title={hasFile ? "Belgeyi önizlemek için tıklayın" : undefined}
                    style={hasFile ? { cursor: "pointer" } : undefined}
                    onClick={hasFile ? () => setPreviewDoc(d) : undefined}
                  >
                    <FileText size={18} className="text-muted flex-shrink-0" />
                    <div className="fw-semibold small">{d.label}</div>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <Badge color={info.color}>{info.label}</Badge>
                    {d.status !== "onayli" && (
                      <>
                        <input
                          id={inputId}
                          type="file"
                          className="d-none"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleUploadDoc(d.key, file);
                            e.target.value = "";
                          }}
                        />
                        <Button
                          color="primary" outline size="sm" disabled={uploadingDoc === d.key}
                          title="Dosya Yükle"
                          onClick={() => document.getElementById(inputId)?.click()}
                        >
                          <Upload size={14} />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </PanelCard>
        );
      */
      case "standDurumu": {
        const counts = clientStands.reduce((acc, s) => {
          const seviye = standDurumuOzetMap[s.id]?.seviye;
          if (seviye) acc[seviye] = (acc[seviye] || 0) + 1;
          return acc;
        }, {});
        return (
          <PanelCard
            title={tx("anasayfa.standDurumu")}
            isOpen={openPanels.standDurumu}
            onToggle={() => togglePanel("standDurumu")}
            onRemove={() => hideWidget("standDurumu")}
            footer={
              <Button color="link" size="sm" onClick={() => navigate("/Firma/Standlar")}>
                {tx("anasayfa.tumStandlariGor")}
              </Button>
            }
          >
            <SplitBar segments={[
              { label: tx("anasayfa.sorunlu"), count: counts.sorunlu || 0, color: "danger" },
              { label: tx("anasayfa.uygun"), count: counts.tamamlandi || 0, color: "success" },
              { label: tx("anasayfa.tamTamam"), count: counts.tam || 0, color: "success" },
            ]} />
            {clientStands.length === 0 ? (
              <div className="text-muted small">{tx("anasayfa.buFuardaSizeBagliMusteri")}</div>
            ) : (
              clientStands.map((s) => (
                <ClientStandRow
                  key={s.id}
                  stand={s}
                  badge={standDurumuBadgeMeta(standDurumuOzetMap[s.id])}
                  onView={() => navigate(`/Firma/Standlar/${s.id}?tab=durumu`)}
                />
              ))
            )}
          </PanelCard>
        );
      }
      case "personeller":
        return (
          <PanelCard
            title={tx("anasayfa.personeller")}
            isOpen={openPanels.personeller}
            onToggle={() => togglePanel("personeller")}
            onRemove={() => hideWidget("personeller")}
            footer={
              <Button color="link" size="sm" onClick={() => navigate("/Firma/Personel")}>
                {tx("anasayfa.sayfayaGit")}
              </Button>
            }
          >
            <SplitBar segments={[
              { label: tx("anasayfa.evrakEksik"), count: notEligibleCount, color: "danger" },
              { label: tx("anasayfa.evrakTamamKartAlamaz"), count: evrakTamamCount, color: "warning" },
              { label: tx("anasayfa.kartAlabilir"), count: eligibleCount, color: "success" },
            ]} />
            {personnel.length === 0 ? (
              <div className="text-muted small">{tx("anasayfa.personelBulunamadi")}</div>
            ) : (
              personnel.map((p) => (
                <PersonnelRow key={p.id} person={p} onView={() => navigate(`/Firma/Personel/${p.id}`)} />
              ))
            )}
          </PanelCard>
        );
      case "standlarim":
        return (
          <PanelCard
            title={tx("anasayfa.standlarim")}
            isOpen={openPanels.standlarim}
            onToggle={() => togglePanel("standlarim")}
            onRemove={() => hideWidget("standlarim")}
            footer={
              <Button color="link" size="sm" onClick={() => navigate("/Firma/Standlar")}>
                {tx("anasayfa.sayfayaGit")}
              </Button>
            }
          >
            <MiniStandChipGrid
              stands={clientStands}
              colorFn={(s) => standlarimColorFor(s, standDurumuOzetMap)}
              onChipClick={(s) => navigate(`/Firma/Standlar/${s.id}`)}
              emptyText={tx("anasayfa.buFuardaSizeBagliMusteri")}
            />
          </PanelCard>
        );
      case "projeler":
        return (
          <PanelCard
            title={tx("anasayfa.projeler")}
            isOpen={openPanels.projeler}
            onToggle={() => togglePanel("projeler")}
            onRemove={() => hideWidget("projeler")}
            footer={
              <Button color="link" size="sm" onClick={() => navigate("/Firma/Projeler")}>
                {tx("anasayfa.sayfayaGit")}
              </Button>
            }
          >
            <MiniStandChipGrid
              stands={clientStands}
              colorFn={projelerColorFor}
              onChipClick={(s) => navigate(`/Firma/Anasayfa/${s.id}`)}
              emptyText={tx("anasayfa.buFuardaSizeBagliStand")}
            />
          </PanelCard>
        );
      case "talepler":
        return (
          <PanelCard
            title={tx("anasayfa.talepler")}
            isOpen={openPanels.talepler}
            onToggle={() => togglePanel("talepler")}
            onRemove={() => hideWidget("talepler")}
            footer={
              <Button color="link" size="sm" onClick={() => navigate("/Firma/Talepler")}>
                {tx("anasayfa.sayfayaGit")}
              </Button>
            }
          >
            <MiniStandChipGrid
              stands={clientStands}
              colorFn={(s) => talepColorFor(s, standDurumuOzetMap)}
              onChipClick={(s) => navigate(`/Firma/Talepler/${s.standId}`)}
              emptyText={tx("anasayfa.buFuardaSizeBagliStand")}
            />
          </PanelCard>
        );
      case "iletisim":
        return (
          <PanelCard
            tour="anasayfa-iletisim"
            title={tx("anasayfa.iletisim")}
            isOpen={openPanels.iletisim}
            onToggle={() => togglePanel("iletisim")}
            onRemove={() => hideWidget("iletisim")}
          >
            {iletisimKisileri.length === 0 ? (
              <div className="text-muted small">{tx("anasayfa.kisiBulunamadi")}</div>
            ) : (
              iletisimKisileri.map((k) => <IletisimRow key={k.id} kisi={k} />)
            )}
          </PanelCard>
        );
      default:
        return null;
    }
  }

  // WAS (until #526): .filter((k) => !hiddenWidgets.includes(k)) — a widget whose module the active set closes is not drawn, whatever the user hid.
  // WAS (until #527): widgetGorunur(k, seviyeOf) — the set's own widget list (Yabancı Katılımcı) is applied too now.
  const gorunur = (k) => !hiddenWidgets.includes(k) && widgetGorunur(k, seviyeOf, setKodu);
  const visibleCol1 = layout.col1.filter(gorunur);
  const visibleCol2 = layout.col2.filter(gorunur);
  const visibleCol3 = layout.col3.filter(gorunur);
  // "Widget Ekle" offers only what the user hid AND the set allows (adding a closed widget back would do nothing).
  const eklenebilirWidgetlar = WIDGET_DEFS.filter((w) => hiddenWidgets.includes(w.key) && widgetGorunur(w.key, seviyeOf, setKodu));

  return (
    <div className="page-content">
      <Container fluid>
        <PortalKisayollari />
        <BreadCrumb title={tx("anasayfa.anaSayfa")} pageTitle={tx("anasayfa.firma")} />
        {/* #558: Yabancı Katılımcı'ya "fuar saatleri dışında alanda kimse yoksa hiçbir şey yüklemeniz gerekmez" (başka setlerde render etmez). */}
        <YabanciKatilimciSahaDisiNotu />

        <div className="d-flex justify-content-end mb-3">
          <Dropdown isOpen={addWidgetOpen} toggle={() => setAddWidgetOpen((o) => !o)}>
            <DropdownToggle
              caret={false}
              color="danger"
              size="sm"
              className="d-flex align-items-center gap-1"
              // WAS (until #526): disabled={hiddenWidgets.length === 0}
              // WAS (until #607): disabled={eklenebilirWidgetlar.length === 0} — Yabancı Katılımcı sees the button but cannot click it.
              // WAS (until #704): disabled={eklenebilirWidgetlar.length === 0 || yabanciKatilimciMi(portal)} — now every company while Sade panel modu is ON too.
              disabled={eklenebilirWidgetlar.length === 0 || sadePanelMi(portal)}
            >
              <Plus size={14} />{" "}{tx("anasayfa.widgetEkle")}
            </DropdownToggle>
            <DropdownMenu end>
              {/* WAS (until #526): WIDGET_DEFS.filter((w) => hiddenWidgets.includes(w.key)).map((w) => ( */}
              {eklenebilirWidgetlar.map((w) => (
                <DropdownItem key={w.key} onClick={() => showWidget(w.key)}>{w.title}</DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
        </div>

        {error && <Alert color="danger">{error}</Alert>}
        {infoMsg && <Alert color="info">{infoMsg}</Alert>}
        {!selectedFuarId && <Alert color="info">{tx("anasayfa.buSayfayiGormekIcinOnce")}</Alert>}

        {loading && (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2">{tx("common:yukleniyor")}</p>
          </div>
        )}

        {!loading && profile && (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
          >
            <Row data-tour="anasayfa-ozet">
              {/* ─── Sol: Firma Profili ─── */}
              <Col lg={4}>
                <Card className="mb-4" data-tour="anasayfa-firma-karti">
                  {/* #667: the same red bar the widget cards carry (PanelCard) with the card's own name; this card is fixed, so no chevron, close or drag. */}
                  <CardHeader className="bg-danger text-white">
                    <span className="fw-semibold small text-uppercase">{tx("firmaKart.firmaKarti")}</span>
                  </CardHeader>
                  <CardBody className="text-center">
                    <div
                      className="d-inline-flex align-items-center justify-content-center bg-light rounded-3 mb-3"
                      style={{ width: 96, height: 96 }}
                    >
                      <Building2 size={40} className="text-danger" />
                    </div>
                    <h5 className="mb-0">{profile.firmaName}</h5>
                    <div className="text-muted small mb-3">{profile.firmaShortName}</div>

                    {/* WAS (until Deniz's 2026-11-04 request): #156 — two tiles here summarised the two company document
                        groups (Firma Belgeleri / Firma İSG Belgeleri, `data-tour="anasayfa-belge-kisayollari"`); the
                        detail and the upload live in Firma Kartı › Evraklar, which the Yüklemeler widget also links to. */}
                    <div className="text-start">
                      <div className="fw-semibold small mb-2">{tx("anasayfa.firmaDetaylari")}</div>
                      <div className="d-flex align-items-start gap-2 mb-2 small">
                        <User size={14} className="text-muted mt-1 flex-shrink-0" />
                        <div><span className="text-muted">{tx("anasayfa.yetkiliDanisman")}</span> {profile.yetkiliDanisman}</div>
                      </div>
                      <div className="d-flex align-items-start gap-2 mb-2 small">
                        <MapPin size={14} className="text-muted mt-1 flex-shrink-0" />
                        <div>{profile.adres}</div>
                      </div>
                      <div className="d-flex align-items-start gap-2 mb-2 small">
                        <Phone size={14} className="text-muted mt-1 flex-shrink-0" />
                        <div>{profile.telefon}</div>
                      </div>
                      <div className="d-flex align-items-start gap-2 mb-2 small">
                        <Mail size={14} className="text-muted mt-1 flex-shrink-0" />
                        <div>{profile.eposta}</div>
                      </div>
                      <div className="d-flex align-items-start gap-2 mb-3 small">
                        <Globe size={14} className="text-muted mt-1 flex-shrink-0" />
                        <div>{profile.webSitesi}</div>
                      </div>
                    </div>

                    <Button color="danger" size="sm" onClick={() => navigate("/Firma/FirmaKart")}>
                      {tx("anasayfa.firmaKartinaGit")}
                    </Button>
                  </CardBody>
                </Card>

                <WidgetColumn id="col1" itemIds={visibleCol1}>
                  {visibleCol1.map((key) => (
                    <SortableWidget key={key} id={key}>{renderWidget(key)}</SortableWidget>
                  ))}
                </WidgetColumn>
              </Col>

              {/* ─── Orta ─── */}
              <Col lg={4}>
                <WidgetColumn id="col2" itemIds={visibleCol2}>
                  {visibleCol2.map((key) => (
                    <SortableWidget key={key} id={key}>{renderWidget(key)}</SortableWidget>
                  ))}
                </WidgetColumn>
              </Col>

              {/* ─── Sağ ─── */}
              <Col lg={4}>
                <WidgetColumn id="col3" itemIds={visibleCol3}>
                  {visibleCol3.map((key) => (
                    <SortableWidget key={key} id={key}>{renderWidget(key)}</SortableWidget>
                  ))}
                </WidgetColumn>
              </Col>
            </Row>
          </DndContext>
        )}

        <DocumentPreviewModal
          isOpen={!!previewDoc}
          toggle={() => setPreviewDoc(null)}
          fileName={previewDoc?.label ? `${previewDoc.label}.pdf` : undefined}
        />
      </Container>
    </div>
  );
}
